import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Phone, Mail, Car, Calendar, MapPin, Clock, Radio } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import AdminLayout from '../components/AdminLayout';
import { formatUkDate } from '../lib/date';
import { DriverReceipts } from '../components/DriverReceipts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  quote_requested: { label: 'Quote Requested', tone: 'bg-amber-100 text-amber-800' },
  quote_sent: { label: 'Quote Sent', tone: 'bg-blue-100 text-blue-800' },
  confirmed: { label: 'Confirmed', tone: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'In Progress', tone: 'bg-indigo-100 text-indigo-800' },
  completed: { label: 'Completed', tone: 'bg-gray-100 text-gray-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-red-100 text-red-700' },
  modification_pending: { label: 'Mod. Pending', tone: 'bg-amber-100 text-amber-800' },
};

const DRIVER_STATUS_META = {
  on_the_way: { label: 'On The Way', tone: 'bg-purple-100 text-purple-800' },
  arrived: { label: 'Arrived', tone: 'bg-blue-100 text-blue-800' },
  pob: { label: 'POB', tone: 'bg-emerald-100 text-emerald-800' },
};

const JobRow = ({ booking }) => {
  const meta = booking.isLive
    ? (DRIVER_STATUS_META[booking.driverStatus] || STATUS_META[booking.status] || { label: booking.status, tone: 'bg-gray-100 text-gray-700' })
    : (STATUS_META[booking.status] || { label: booking.status, tone: 'bg-gray-100 text-gray-700' });
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-3 px-2 rounded-md hover:bg-gray-50" data-testid={`driver-job-${booking.id}`}>
      <div className="col-span-2">
        <p className="text-xs uppercase text-gray-500 tracking-wider">{booking.serviceType?.split(' ')[0]}</p>
        <p className="font-semibold text-black text-sm">{booking.pickupTime}</p>
        <p className="text-xs text-gray-600 flex items-center gap-1">
          <Calendar className="h-3 w-3" /> {formatUkDate(booking.pickupDate)}
        </p>
      </div>
      <div className="col-span-7 min-w-0">
        <p className="text-sm text-black font-medium truncate flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-gray-400" />
          {booking.pickupLocation}
        </p>
        <p className="text-xs text-gray-600 truncate pl-5">&rarr; {booking.dropoffLocation}</p>
        <p className="text-xs text-gray-500 truncate pl-5">{booking.firstName} {booking.lastName}</p>
      </div>
      <div className="col-span-3 flex flex-col items-end gap-1.5">
        {booking.isLive && (
          <span className="flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
            <Radio className="h-3 w-3" /> LIVE
          </span>
        )}
        <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
    </div>
  );
};

const DriverDetail = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/drivers/${encodeURIComponent(driverId)}/jobs`);
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load driver');
      } finally {
        setLoading(false);
      }
    })();
  }, [driverId]);

  const driver = data?.driver;

  return (
    <AdminLayout
      title={driver?.name || 'Driver'}
      subtitle={driver?.email}
      actions={
        <Button onClick={() => navigate('/admin/drivers')} variant="outline" className="border-gray-300">
          <ArrowLeft className="h-4 w-4 mr-2" /> All Drivers
        </Button>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : !driver ? (
        <p className="text-sm text-gray-700">Driver not found.</p>
      ) : (
        <div className="space-y-6">
          {/* Profile + colour key */}
          <Card className="p-6">
            <div className="flex flex-wrap items-center gap-6 justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="h-14 w-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-sm"
                  style={{ backgroundColor: driver.color }}
                  data-testid="driver-avatar"
                >
                  {driver.name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{driver.name}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                    {driver.phone && (
                      <a href={`tel:${driver.phone}`} className="flex items-center gap-1.5 hover:text-black">
                        <Phone className="h-3.5 w-3.5" /> {driver.phone}
                      </a>
                    )}
                    {driver.email && (
                      <a href={`mailto:${driver.email}`} className="flex items-center gap-1.5 hover:text-black">
                        <Mail className="h-3.5 w-3.5" /> {driver.email}
                      </a>
                    )}
                    {driver.vehicle && (
                      <span className="flex items-center gap-1.5">
                        <Car className="h-3.5 w-3.5" /> {driver.vehicle}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-sm flex items-center gap-2" data-testid="driver-color-key">
                <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: driver.color }} />
                <span className="text-gray-700">Map key colour</span>
              </div>
            </div>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Live Now', value: data.stats.live, tone: 'text-emerald-700' },
              { label: 'Upcoming', value: data.stats.upcoming, tone: 'text-black' },
              { label: 'Past', value: data.stats.past, tone: 'text-gray-700' },
              { label: 'Total Assigned', value: data.stats.total, tone: 'text-black' },
            ].map((s) => (
              <Card key={s.label} className="p-5">
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-3xl font-bold mt-1 ${s.tone}`}>{s.value}</p>
              </Card>
            ))}
          </div>

          {/* Live jobs */}
          {data.live.length > 0 && (
            <Card className="p-6 border-l-4" style={{ borderLeftColor: driver.color }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-black flex items-center gap-2">
                  <Radio className="h-5 w-5 text-emerald-600" />
                  Live Now
                </h2>
                <Button size="sm" variant="outline" onClick={() => navigate('/admin/tracking')} className="border-gray-300">
                  Open Tracking Map
                </Button>
              </div>
              <div className="divide-y divide-gray-100" data-testid="driver-live-list">
                {data.live.map((b) => <JobRow key={b.id} booking={b} />)}
              </div>
            </Card>
          )}

          {/* Upcoming */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-black flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-500" />
                Upcoming Jobs
              </h2>
              <span className="text-xs text-gray-500">{data.upcoming.length} scheduled</span>
            </div>
            {data.upcoming.length === 0 ? (
              <p className="text-sm text-gray-500">No upcoming jobs assigned.</p>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="driver-upcoming-list">
                {data.upcoming.map((b) => <JobRow key={b.id} booking={b} />)}
              </div>
            )}
          </Card>

          {/* Past */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-black mb-3">Past Jobs</h2>
            {data.past.length === 0 ? (
              <p className="text-sm text-gray-500">No past jobs yet.</p>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="driver-past-list">
                {data.past.slice(0, 20).map((b) => <JobRow key={b.id} booking={b} />)}
                {data.past.length > 20 && (
                  <p className="text-xs text-gray-500 pt-3">Showing 20 of {data.past.length} past jobs.</p>
                )}
              </div>
            )}
          </Card>

          <DriverReceipts driverId={driverId} driverName={driver.name} />
        </div>
      )}
    </AdminLayout>
  );
};

export default DriverDetail;
